import Auth from "src/Auth";
import {
  manuscriptChangeActionResponseSchema,
  manuscriptChangeApproveRequestSchema,
  manuscriptChangeConflictResolutionRequestSchema,
  manuscriptChangeDeclineRequestSchema,
  manuscriptChangeModifyRequestSchema,
  manuscriptChangeRetryRequestSchema,
  manuscriptChangeVisibleRequestSchema,
  manuscriptChangeSetApplyRequestSchema,
  manuscriptChangeSetUndoRequestSchema,
  manuscriptChangeSetActionResponseSchema,
  type ManuscriptChangeActionResponse,
  type ManuscriptChangeApproveRequest,
  type ManuscriptChangeConflictResolutionRequest,
  type ManuscriptChangeDeclineRequest,
  type ManuscriptChangeModifyRequest,
  type ManuscriptChangeRetryRequest,
  type ManuscriptChangeVisibleRequest,
  type ManuscriptChangeSetApplyRequest,
  type ManuscriptChangeSetUndoRequest,
  type ManuscriptChangeSetActionResponse,
} from "~shared/agenticChat/requestSchemas";

export function safeManuscriptChangeApplyFailureMessage(_error: unknown): string {
  return "I couldn’t apply that change. No document changes were made. Please try again.";
}

async function postManuscriptChangeAction(
  path: string,
  body: Record<string, unknown>,
): Promise<ManuscriptChangeActionResponse> {
  const response = await Auth.fetchWithIdToken(path, {
    method: "POST",
    ...body,
  });
  const parsed = manuscriptChangeActionResponseSchema.safeParse(response);
  if (!parsed.success) {
    throw new Error("Invalid manuscript change action response");
  }
  return parsed.data;
}

async function postManuscriptChangeSetAction(
  path: string,
  body: Record<string, unknown>,
): Promise<ManuscriptChangeSetActionResponse> {
  const response = await Auth.fetchWithIdToken(path, { method: "POST", ...body });
  const parsed = manuscriptChangeSetActionResponseSchema.safeParse(response);
  if (!parsed.success) throw new Error("Invalid manuscript change-set response");
  return parsed.data;
}

export async function approveManuscriptChange(
  request: ManuscriptChangeApproveRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeApproveRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/approve",
    body,
  );
}

export async function declineManuscriptChange(
  request: ManuscriptChangeDeclineRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeDeclineRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/decline",
    body,
  );
}

export async function retryManuscriptChange(
  request: ManuscriptChangeRetryRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeRetryRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/retry",
    body,
  );
}

export async function modifyManuscriptChange(
  request: ManuscriptChangeModifyRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeModifyRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/modify",
    body,
  );
}

export async function resolveManuscriptChangeConflict(
  request: ManuscriptChangeConflictResolutionRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeConflictResolutionRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/conflict",
    body,
  );
}

export async function acknowledgeManuscriptChangeVisible(
  request: ManuscriptChangeVisibleRequest,
): Promise<ManuscriptChangeActionResponse> {
  const body = manuscriptChangeVisibleRequestSchema.parse(request);
  return postManuscriptChangeAction(
    "/api/ai/agenticChat/manuscript-change/visible",
    body,
  );
}

export async function applyManuscriptChangeSet(
  request: ManuscriptChangeSetApplyRequest,
): Promise<ManuscriptChangeSetActionResponse> {
  const body = manuscriptChangeSetApplyRequestSchema.parse(request);
  return postManuscriptChangeSetAction(
    "/api/ai/agenticChat/manuscript-change-set/apply",
    body,
  );
}

export async function undoManuscriptChangeSet(
  request: ManuscriptChangeSetUndoRequest,
): Promise<ManuscriptChangeSetActionResponse> {
  const body = manuscriptChangeSetUndoRequestSchema.parse(request);
  return postManuscriptChangeSetAction(
    "/api/ai/agenticChat/manuscript-change-set/undo",
    body,
  );
}
